import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  AppState,
  type AppStateStatus,
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Ionicons } from '@expo/vector-icons';
import { DynamicTitle } from '../DynamicTitle';
import {
  enableLocationBypassForDevTesting,
  getForegroundLocationAccessSnapshot,
  isLocationBypassForSessionAsync,
  isLocationVerifiedThisSessionAsync,
  requestLocationAndVerify,
  setLocationVerifiedThisSession,
} from '../../lib/utils/locationAvailability';

const MONITOR_INTERVAL_MS = 12_000;

type Phase = 'boot' | 'onboarding' | 'ready';

interface ComensalLocationGateProps {
  children: React.ReactNode;
}

export function ComensalLocationGate({ children }: ComensalLocationGateProps) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('boot');
  const [busy, setBusy] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);

  const goToBlocked = useCallback(async () => {
    await setLocationVerifiedThisSession(false);
    navigate('/ubicacion-requerida', { replace: true });
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBootError(null);
      try {
        if (await isLocationBypassForSessionAsync()) {
          if (!cancelled) setPhase('ready');
          return;
        }
        if (await isLocationVerifiedThisSessionAsync()) {
          const snap = await getForegroundLocationAccessSnapshot();
          if (cancelled) return;
          if (snap.ok) {
            setPhase('ready');
            return;
          }
          await goToBlocked();
          return;
        }
        if (!cancelled) setPhase('onboarding');
      } catch {
        if (!cancelled) setBootError('No se pudo comprobar la ubicación.');
        if (!cancelled) setPhase('onboarding');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [goToBlocked]);

  useEffect(() => {
    if (phase !== 'ready') return;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      const r = await getForegroundLocationAccessSnapshot();
      if (cancelled || r.ok) return;
      await goToBlocked();
    };

    const id = setInterval(tick, MONITOR_INTERVAL_MS);

    const onVis = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        tick();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
    }

    const sub = AppState.addEventListener('change', (s: AppStateStatus) => {
      if (s === 'active') tick();
    });

    return () => {
      cancelled = true;
      clearInterval(id);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
      sub.remove();
    };
  }, [phase, goToBlocked]);

  const handleBypassDev = async () => {
    await enableLocationBypassForDevTesting();
    setPhase('ready');
  };

  const handleContinuar = async () => {
    setBusy(true);
    try {
      const r = await requestLocationAndVerify();
      if (r.ok) {
        await setLocationVerifiedThisSession(true);
        setPhase('ready');
      } else {
        await goToBlocked();
      }
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'boot') {
    return (
      <View style={styles.centered}>
        <DynamicTitle title="Comprobando ubicación — As Gastronómico" />
        <ActivityIndicator size="large" color="#030213" />
        {bootError ? <Text style={styles.bootErr}>{bootError}</Text> : null}
        <TouchableOpacity style={styles.bypassBtn} onPress={handleBypassDev} activeOpacity={0.75}>
          <Text style={styles.bypassText}>Bypass ubicación (pruebas)</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (phase === 'onboarding') {
    return (
      <View style={styles.screen}>
        <DynamicTitle title="Ubicación — As Gastronómico" />
        <View style={styles.iconWrap}>
          <Ionicons name="navigate-circle-outline" size={56} color="#030213" />
        </View>
        <Text style={styles.title}>¡Voto válido solo en el local!</Text>
        <Text style={styles.body}>
          Para que la app te muestre tu ciudad y el contenido acorde a tu zona, necesitamos que el GPS esté encendido y que permitas el
          acceso a la ubicación en este dispositivo o navegador.
        </Text>
        <Text style={styles.sub}>
          Después podrás usar el resto del área de comensal. Comprobaremos de forma periódica que la ubicación sigue disponible.
        </Text>

        <TouchableOpacity
          style={[styles.primaryBtn, busy && styles.primaryBtnDisabled]}
          onPress={handleContinuar}
          disabled={busy}
          activeOpacity={0.85}
        >
          {busy ? (
            <ActivityIndicator color="#ffffff" />
          ) : (
            <>
              <Ionicons name="checkbox-outline" size={22} color="#ffffff" style={styles.btnIcon} />
              <Text style={styles.primaryBtnText}>Ubicación encendida — continuar</Text>
            </>
          )}
        </TouchableOpacity>

        <TouchableOpacity style={styles.bypassBtnOnboarding} onPress={handleBypassDev} activeOpacity={0.75}>
          <Text style={styles.bypassText}>Bypass ubicación (pruebas)</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, minHeight: 0 }}>{children}</View>
  );
}

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    padding: 24,
  },
  bootErr: {
    marginTop: 16,
    fontSize: 14,
    color: '#b91c1c',
    textAlign: 'center',
  },
  screen: {
    flex: 1,
    backgroundColor: '#ffffff',
    paddingHorizontal: 24,
    paddingTop: 48,
    paddingBottom: 32,
  },
  iconWrap: {
    alignSelf: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#030213',
    textAlign: 'center',
    marginBottom: 12,
  },
  body: {
    fontSize: 15,
    color: '#374151',
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 12,
  },
  sub: {
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 19,
    textAlign: 'center',
    marginBottom: 28,
  },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#030213',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 10,
    minHeight: 52,
  },
  primaryBtnDisabled: {
    opacity: 0.7,
  },
  btnIcon: {
    marginRight: 8,
  },
  primaryBtnText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  bypassBtn: {
    marginTop: 28,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  bypassBtnOnboarding: {
    marginTop: 20,
    alignSelf: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  bypassText: {
    fontSize: 13,
    color: '#9ca3af',
    textDecorationLine: 'underline',
    textAlign: 'center',
  },
});
